import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  Box,
  LinearProgress,
  Typography,
  Button,
  TableRow,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableCell,
  TableBody,
} from "@mui/material";
import { CancelManager } from "../../lib/cancelManager.ts";

export type ImportType = "Create" | "Update";
export type ImportProgressStatus =
  | "Import"
  | "Upload"
  | "Success"
  | "Error"
  | "Cancel";

export interface IImportProgressInformation {
  total: number;
  imported: number;
  logs: {
    title: string;
    type: ImportType;
    status: ImportProgressStatus;
    message: string;
    link: string;
  }[];
}

interface ImportProgressModalProps {
  open: boolean;
  onClose: () => void;
  totalCount: number;
  info: IImportProgressInformation;
}

export const ImportProgressModal: React.FC<ImportProgressModalProps> = ({
  open,
  onClose,
  totalCount,
  info,
}) => {
  const [isCancel, setIsCancel] = useState<boolean>(false);
  const tableRef = useRef<HTMLDivElement>(null);

  const handleCancelImport = () => {
    const cancelManager = CancelManager.getInstance();
    cancelManager.cancel();
    setIsCancel(true);
  };

  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.scrollTop = tableRef.current.scrollHeight;
    }
  }, [info.logs.length]);

  const getTypeText = (type: ImportType) => {
    switch (type) {
      case "Create":
        return "新規作成";
      case "Update":
        return "更新";
    }
  };

  const getStatusColor = (status: ImportProgressStatus) => {
    switch (status) {
      case "Success":
        return "success.main";
      case "Error":
        return "error.main";
      case "Cancel":
        return "warning.main";
      default:
        return "text.primary";
    }
  };

  const getStatusText = (status: ImportProgressStatus) => {
    switch (status) {
      case "Success":
        return "完了";
      case "Error":
        return "エラー";
      case "Cancel":
        return "キャンセル";
      case "Import":
        return "インポート中";
      case "Upload":
        return "アップロード中";
    }
  };

  return (
    <Modal open={open}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 800,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          outline: "none",
        }}
      >
        <Typography variant="h6" component="h2">
          データインポート進捗
        </Typography>
        <LinearProgress
          variant="determinate"
          value={(info.logs.length / totalCount) * 100}
          sx={{ my: 2 }}
        />
        <Typography variant="body1">
          {info.logs.length}/{totalCount} 行処理済
        </Typography>
        <TableContainer
          component={Paper}
          sx={{ maxHeight: 300, overflow: "auto", my: 2 }}
          ref={tableRef}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>行</TableCell>
                <TableCell>記事タイトル</TableCell>
                <TableCell>タイプ</TableCell>
                <TableCell>状況</TableCell>
                <TableCell>メッセージ</TableCell>
                <TableCell>リンク</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {info.logs.map((log, index) => (
                <TableRow key={index}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{log.title}</TableCell>
                  <TableCell>{getTypeText(log.type)}</TableCell>
                  <TableCell sx={{ color: getStatusColor(log.status) }}>
                    {getStatusText(log.status)}
                  </TableCell>
                  <TableCell>{log.message}</TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      href={log.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      disabled={!log.link}
                    >
                      開く
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ mt: 2, textAlign: "right" }}>
          <Button
            variant="contained"
            onClick={handleCancelImport}
            disabled={totalCount === info.logs.length || isCancel}
            sx={{ mr: 2 }}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={onClose}
            disabled={totalCount !== info.logs.length && !isCancel}
          >
            閉じる
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
